import { WarningFilled } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { DatePickerInput } from '@components/_SiteWide/Form/DatePickerInput'
import { FormNumberInput } from '@components/_SiteWide/Form/FormNumberInput'
import { TimePickerInput } from '@components/_SiteWide/Form/TimePickerInput'
import api from '@utils/api'
import { Button, Col, Form, Input, Modal, notification, Row } from 'antd'
import React, { useEffect, useState } from 'react'
import { formatters } from '@utils/intl/dateTimeFormatters'

export function TankReadingModal({
  form,
  storeNumber,
  tankId,
  tankReading,
  showingTankModal,
  manualEntry,
  setTankReading,
  closeTankModal,
  saveTankReading,
  setManualEntry,
}) {
  const [chartDetails, setChartDetails] = useState()
  const [loadingData, setLoadingData] = useState(true)
  const [invalidInches, setInvalidInches] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (showingTankModal) {
      api.getTankChartDetails(storeNumber, tankId).then(({ data }) => {
        setChartDetails(data)
        setLoadingData(false)
      })
    }
    form.setFieldsValue({ ...tankReading })
  }, [showingTankModal])

  if (loadingData) return null

  const onInchChange = async (value) => {
    if (manualEntry || !chartDetails) return
    if (!value || value.toString() === '') {
      setTankReading({ ...tankReading, inches: null, volume: null })
      return
    }

    if (value < chartDetails.min_inches || value > chartDetails.max_inches) {
      setInvalidInches(true)
      setTankReading({ ...tankReading, inches: null, volume: null })
    } else {
      const resp = await api.convertInchesToGallons(storeNumber, tankId, parseFloat(value))
      setInvalidInches(false)
      setTankReading({ ...tankReading, inches: value, volume: resp.data })
    }
  }

  return (
    <Form form={form} id='tank-reading-form'>
      <Modal
        title='Enter Manual Reading'
        width={1000}
        visible={showingTankModal}
        onOk={closeTankModal}
        onCancel={closeTankModal}
        className='tank-reading-modal'
        footer={[
          <Button key='cancel' onClick={closeTankModal} disabled={isSaving}>
            Cancel
          </Button>,
          <Button
            key='submit'
            htmlType='submit'
            className='success'
            disabled={isSaving}
            loading={isSaving}
            onClick={() => {
              setIsSaving(true)
              form
                .validateFields()
                .then((values) => {
                  saveTankReading(values, () => setIsSaving(false)).catch((e) => {
                    notification.error({
                      message: 'Value Error saving manual tank reading',
                      duration: 8,
                    })
                    setIsSaving(false)
                  })
                })

                .catch((info) => {
                  setIsSaving(false)
                  notification.error({
                    message: 'Error saving manual tank reading.',
                    duration: 8,
                  })
                })
            }}
          >
            Save
          </Button>,
        ]}
      >
        {tankReading && (
          <ModalContent
            tankReading={tankReading}
            chartDetails={chartDetails}
            invalidInches={invalidInches}
            manualEntry={manualEntry}
            setManualEntry={setManualEntry}
            setChartDetails={setChartDetails}
            onInchChange={onInchChange}
          />
        )}
      </Modal>
    </Form>
  )
}

function ModalContent({
  tankReading,
  chartDetails,
  invalidInches,
  manualEntry,
  setManualEntry,
  setChartDetails,
  onInchChange,
}) {
  const { formatVolume } = useFormatter()
  const formattedValue = formatVolume(tankReading.volume)
  const formattedUnit = formatVolume(null, false)?.toLowerCase()
  return (
    <>
      <Row className={`${chartDetails ? 'strapping' : ''} tank-reading-row-header p-3`}>
        <Col span={12}>
          <span className='store mr-1 pt-1'>{tankReading.store}</span>
          <span className='label mr-1'>-</span>
          <span className='label'>{tankReading.name}</span>
        </Col>
        <Col span={12} align='right'>
          <span className='mr-3'>TANK PRODUCT</span>
          <span className='value'>{tankReading.product}</span>
        </Col>
      </Row>
      {!chartDetails && !manualEntry && (
        <div className='tank-reading-no-strapping pt-2 pb-2'>
          <WarningFilled className='mt-1 mr-2' />
          This tank is not linked to a tank chart, you will have to enter {formattedUnit} manually.
        </div>
      )}
      {chartDetails && (
        <div className='tank-reading-strapping p-2'>
          <div>
            <span className='mr-3 label'>TANK CHART</span>
            <span className='value'>{chartDetails.name}</span>
          </div>
          <div>
            <span className='mr-3 label'>INCH RANGE</span>
            <span className='value'>{`${chartDetails.min_inches} - ${chartDetails.max_inches}`}</span>
          </div>
        </div>
      )}
      <div className='label-header label pl-4 pr-4 pt-5 pb-0'>
        <div>Enter Tank Reading</div>
        {manualEntry && chartDetails && (
          <div
            onClick={() => {
              setManualEntry(false)
              setChartDetails(chartDetails)
            }}
            className='manual-inch-conversion'
          >
            Use Tank Chart
          </div>
        )}
        {!manualEntry && chartDetails && (
          <div
            onClick={() => {
              setManualEntry(true)
            }}
            className='manual-inch-conversion'
          >
            Manual Inch Conversion
          </div>
        )}
      </div>
      <Row className='p-4 pt-0'>
        <Col span={6}>
          <DatePickerInput
            defaultValue={tankReading.date}
            editable
            name='date'
            required
            message='Date is required'
            style={{ width: '90%' }}
            format={formatters.DATE}
          />
        </Col>
        <Col span={6}>
          <TimePickerInput
            initialValue={tankReading.time}
            editable
            name='time'
            required
            message='Time is required'
            style={{ width: '90%' }}
            format={formatters.TIME}
          />
        </Col>
        <Col span={6}>
          <FormNumberInput
            onChange={onInchChange}
            placeholder='Enter Inches'
            editable
            name='inches'
            required
            message='Inches are required'
            style={{ width: '90%' }}
          />
        </Col>
        <Col span={6}>
          {!manualEntry && chartDetails ? (
            <>
              <Form.Item name='volume' style={{ display: 'none' }}>
                <Input type='text' value={tankReading.volume} />
              </Form.Item>
              <span className='strapping-gallons mt-1'>
                {tankReading.volume && tankReading.volume.toString().trim() !== '' ? formattedValue : 'N/A'}
              </span>
            </>
          ) : (
            <FormNumberInput
              placeholder='Enter Volume'
              editable
              name='volume'
              required
              message='Volume is required'
              style={{ width: '90%' }}
            />
          )}
        </Col>
      </Row>
      {invalidInches && (
        <div className='p-4 invalid-inches'>
          Your inch reading was outside the range of the tank chart. If you believe this is incorrect, you can enter it
          by clicking 'Manaual Inch Conversion.'
        </div>
      )}
    </>
  )
}
